import { ReactNode, useContext, useRef } from "react";

import { useDispatch } from "react-redux";

import { AiOutlineInfo } from "react-icons/ai";

import { useTranslator } from "@eo-locale/react";

import {
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerCloseButton,
  HStack,
  FiMap,
  IconButton,
  Spacer,
  FiList
} from "@/storybook-components";
import { useAppSelector } from "@/redux/store/hooks";
import SortPins from "./sort-pins";
import MapDetails from "./help";
import WrapperComponent from "./wrapper-component";
import { resetMarker } from "@/redux/features/place-marker/slice";
import { setPinDetails } from "@/redux/features/place/slice";

import { EsriContext } from "@/components/places/context/esri-context";
import { EsriContextType } from "@/components/places/context/esri-context-type";

interface ESRIPanelProps {
  children: ReactNode;
  isOpen: boolean;
  handleCloseDrawer: () => void;
  setInteractionsVisibility: (param: any) => void;
  handleActivityFeedClick: any;
  rightPanelProps: any;
}

const EsriDrawer = ({
  children,
  isOpen,
  handleCloseDrawer,
  setInteractionsVisibility,
  handleActivityFeedClick,
  rightPanelProps
}: ESRIPanelProps) => {
  const rightPanelView = useAppSelector(
    (state) => state.placesRightPanelView.view
  );
  const dispatch = useDispatch();
  const btnRef = useRef(null);
  const { translate } = useTranslator();
  const { openInfoModal } = useContext(EsriContext) as EsriContextType;

  const activityFeedBtnProps = {
    size: "lg",
    variant: "solid",
    borderRadius: "50%",
    bg: "white",
    color: "gray.900",
    boxShadow: "lg",
    _hover: { background: "gray.100", color: "gray.900" },
    className: "activity-feed-button",
    "aria-label": translate("places.feed")
  };

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      size="lg"
      onClose={handleCloseDrawer}
    >
      <DrawerContent backgroundColor="gray.100">
        <DrawerCloseButton mt={"1rem"} mr={"0.75rem"} size={"md"} />
        <DrawerBody p={0}>
          <Box
            id="pins-list-on-mobile"
            height="100svh"
            overflowY={
              ["activity-feed", "view-pin-details"].includes(rightPanelView)
                ? "auto"
                : "hidden"
            }
          >
            {children}
          </Box>
          <HStack
            as="section"
            justifyContent="space-between"
            position="fixed"
            bottom={{ base: 4, xl: "auto" }}
            top={{ base: "auto", xl: 4 }}
            left={{ base: "0px", lg: "20px", sm: "20px" }}
            zIndex={5}
          >
            <Box display="flex" gap={8}>
              {!["create-pin", "edit-pin"].includes(rightPanelView) && (
                <>
                  <MapDetails rightPanelProps={rightPanelProps} />
                  <IconButton
                    ref={btnRef}
                    size="lg"
                    boxShadow="lg"
                    variant="solid"
                    borderRadius="50%"
                    bg="white"
                    color="gray.900"
                    fontSize="1.25rem"
                    _hover={{ background: "gray.100", color: "gray.900" }}
                    onClick={openInfoModal}
                    icon={<AiOutlineInfo />}
                    aria-label={translate("places.infoIcon")}
                  />
                  <IconButton
                    aria-label={translate("places.goBackToMap")}
                    icon={<FiMap />}
                    size="lg"
                    variant="solid"
                    borderRadius="50%"
                    bg={"white"}
                    color="gray.900"
                    boxShadow="lg"
                    _hover={{ background: "gray.100", color: "gray.900" }}
                    onClick={() => {
                      handleCloseDrawer();
                      setInteractionsVisibility(true);
                      dispatch(resetMarker());
                      dispatch(setPinDetails({}));
                    }}
                    className="activity-feed-button"
                  />
                </>
              )}
              <Spacer />
            </Box>
          </HStack>

          {!["create-pin", "view-pin-details", "edit-pin"].includes(
            rightPanelView
          ) && (
            <WrapperComponent
              bottom={{ base: 4, xl: "auto" }}
              top={{ base: "auto", xl: 4 }}
              position="absolute"
              right={{ base: "0px", lg: "calc(30vw + 20px)", sm: "0px" }}
              zIndex={999}
            >
              <Box display="flex">
                <Box pr={4}>
                  <SortPins />
                </Box>
              </Box>
            </WrapperComponent>
          )}
          {!["create-pin", "edit-pin", "activity-feed"].includes(
            rightPanelView
          ) && (
            <WrapperComponent
              bottom={{ base: 4, xl: "auto" }}
              top={{ base: "auto", xl: 4 }}
              position="absolute"
              right={{ base: "0px", lg: "calc(30vw + 20px)", sm: "0px" }}
              zIndex={999}
            >
              <Box display="flex">
                <Box pr={4}>
                  <IconButton
                    icon={<FiList />}
                    onClick={() => {
                      handleActivityFeedClick();
                    }}
                    {...activityFeedBtnProps}
                  />
                </Box>
              </Box>
            </WrapperComponent>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default EsriDrawer;
